import { useState, useMemo } from 'react';
import { format, isToday, isYesterday, isThisWeek, parseISO } from 'date-fns';
import { NotificationItem } from './NotificationItem';
import { NotificationEmpty } from './NotificationEmpty';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  metadata: {
    bookingId?: number;
    videoId?: number;
    actionUrl?: string;
    priority?: 'low' | 'medium' | 'high';
  };
  readAt?: string | null;
  read_at?: string | null;
  createdAt?: string;
  created_at?: string;
}

interface NotificationListProps {
  notifications: Notification[];
  isLoading: boolean;
  hasMore: boolean;
  filter: 'all' | 'unread';
  onLoadMore: () => void;
  onNotificationClick: (notification: Notification) => void;
  onMarkAsRead: (id: number) => void;
  onMarkAllAsRead: () => void;
}

export function NotificationList({
  notifications,
  isLoading,
  hasMore,
  filter,
  onLoadMore,
  onNotificationClick,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationListProps) {
  const groupedNotifications = useMemo(() => {
    const groups: Record<string, Notification[]> = {};
    
    if (!notifications || !Array.isArray(notifications)) {
      return groups;
    }
    
    notifications.forEach(notification => {
      if (!notification) return;
      
      // Handle both camelCase and snake_case field names
      const createdAt = notification.createdAt || notification.created_at;
      if (!createdAt) return;
      
      const date = parseISO(createdAt);
      let groupKey: string;
      
      if (isToday(date)) {
        groupKey = 'Today';
      } else if (isYesterday(date)) {
        groupKey = 'Yesterday';
      } else if (isThisWeek(date)) {
        groupKey = 'This Week';
      } else {
        groupKey = format(date, 'MMMM yyyy');
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(notification);
    });
    
    return groups;
  }, [notifications]);

  const hasUnread = notifications.some(n => !n.readAt && !n.read_at);

  if (isLoading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (notifications.length === 0) {
    return <NotificationEmpty filter={filter} />;
  }

  return (
    <div className="flex flex-col">
      {hasUnread && filter === 'all' && (
        <div className="px-4 py-2 border-b border-white/10">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllAsRead}
            className="text-xs text-gray-400 hover:text-white"
          >
            Mark all as read
          </Button>
        </div>
      )}
      
      <div>
        {notifications.map((notification, index) => (
          <div key={notification.id}>
            <NotificationItem
              notification={notification}
              onClick={onNotificationClick}
              onMarkAsRead={onMarkAsRead}
            />
            {index < notifications.length - 1 && (
              <div className="border-b border-white/10" />
            )}
          </div>
        ))}
      </div>
      
      {hasMore && (
        <div className="p-4 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoading}
            className="text-gray-400 hover:text-white border-white/10"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load more'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}